import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { APIOrganization, APIUser } from '@visorpro/client'
import { APIOrganizationMemberRole } from '@visorpro/client'
import {
    Chip,
    ListItemText,
    MenuItem,
    Select,
    type SelectChangeEvent,
    FormControl,
    InputLabel,
    Box,
    Button,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    Stack,
    DialogActions,
} from '@mui/material'
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table'
import _ from 'lodash'
import { type DateTime } from 'luxon'
import { stringify } from 'csv-stringify/browser/esm/sync'
import { formatDate } from '../util/format'
import { type SearchableSelectOption } from './searchable-select'
import { useAdminMaterialReactTable } from './table/mui-table'
import { useMultiValueColumnDef } from './table/column-definition'
import { SplitButtons } from './split-button'
import { exportFile } from '../util/export'
import { CorporateFare, Info } from '@mui/icons-material'

export interface UsersTableProps {
    users: APIUser[]
    allUsers?: APIUser[]
    organizations?: APIOrganization[]
    // The organization whose membership role is shown/edited. Required for the
    // Role column, which resolves each user's membership within this org.
    organizationId?: string
    setOrganizations?: (
        userId: string,
        addedOrganizationIds: string[],
        removedOrganizationIds: string[],
    ) => void
    isLoading?: boolean
    showProgressBars?: boolean
    isPrimaryBackgroundColor?: boolean
    addMember?: (userId: string) => void
    removeMember?: (userId: string) => void
    removeMembers?: (userIds: string[]) => void
    updateMemberRole?: (
        userId: string,
        role: APIOrganizationMemberRole,
    ) => void
}

export const UsersTable = ({
    users,
    allUsers,
    organizations,
    organizationId,
    setOrganizations,
    isLoading,
    showProgressBars,
    isPrimaryBackgroundColor,
    addMember,
    removeMember,
    removeMembers,
    updateMemberRole,
}: UsersTableProps) => {
    const [isAddingMember, setIsAddingMember] = useState(false)

    const getOrganizationOptions = (
        organizations: APIOrganization[],
    ): SearchableSelectOption[] => {
        return organizations.map((org) => ({ label: org.name, value: org.id }))
    }

    const organizationColumnDef = useMultiValueColumnDef<
        APIUser,
        APIOrganization
    >({
        fieldName: 'Organizations',
        optionsData: organizations,
        renderChip: (organization, index) => (
            <Link
                className="text-link"
                to={`/organizations/${organization.id}/members`}
                key={index}>
                <Chip
                    label={organization.name}
                    size="small"
                    title={organization.name}
                    clickable
                    icon={<CorporateFare />}
                />
            </Link>
        ),
        getOptions: getOrganizationOptions,
        onEdit: setOrganizations,
    })

    const columns = useMemo<MRT_ColumnDef<APIUser>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                enableClickToCopy: true,
            },
            {
                accessorFn: (user) => [user.first_name, user.last_name].join(' '),
                id: 'name',
                header: 'Name',
                size: 200,
            },
            {
                accessorFn: (user) => user.email || user.phone_number,
                id: 'credential',
                header: 'Credential',
                enableClickToCopy: true,
                size: 200,
            },
            // Role within this organization. Only shown on the org-members page,
            // where both an organization context and an update handler exist.
            ...(updateMemberRole && organizationId
                ? [
                      {
                          accessorFn: (user: APIUser) =>
                              user.memberships?.find(
                                  (membership) =>
                                      membership.organization_id ===
                                      organizationId,
                              )?.role,
                          id: 'role',
                          header: 'Role',
                          enableEditing: true,
                          Edit: ({ cell, row, table }) => {
                              const currentRole =
                                  cell.getValue<APIOrganizationMemberRole>()
                              const userId = row.id
                              const roles: string[] = Object.values(
                                  APIOrganizationMemberRole,
                              )

                              const [role, setRole] = useState(currentRole)
                              const onChange = (
                                  event: SelectChangeEvent<string>,
                              ) => {
                                  setRole(
                                      event.target
                                          .value as APIOrganizationMemberRole,
                                  )
                              }
                              const onBlur = () => {
                                  if (role && role !== currentRole) {
                                      updateMemberRole(userId, role)
                                  }
                                  table.setEditingCell(null)
                              }

                              return (
                                  <Select
                                      fullWidth
                                      required
                                      size="small"
                                      variant="standard"
                                      defaultValue={currentRole}
                                      onChange={onChange}
                                      onBlur={onBlur}>
                                      {roles.map((role) => (
                                          <MenuItem key={role} value={role}>
                                              {role}
                                          </MenuItem>
                                      ))}
                                  </Select>
                              )
                          },
                          enableColumnFilter: true,
                          filterVariant: 'select',
                          filterSelectOptions: Object.values(
                              APIOrganizationMemberRole,
                          ),
                      } as MRT_ColumnDef<APIUser>,
                  ]
                : []),
            {
                accessorFn: (user) =>
                    user.memberships?.map(
                        (membership) => membership.organization!,
                    ) ?? [],
                id: 'organizations',
                header: 'Organizations',
                visibleInShowHideMenu: organizations !== undefined,
                filterFn: (row, _columnId, filterValues: string[]) =>
                    row.original.memberships?.some((membership) =>
                        filterValues.some(
                            (filterValue) =>
                                membership.organization_id === filterValue,
                        ),
                    ) ?? false,
                ...organizationColumnDef,
            },
            {
                accessorKey: 'auth0_sub',
                header: 'Auth0 Sub',
                enableClickToCopy: true,
            },
            {
                accessorFn: (user) => user.updated_at,
                id: 'updated_at',
                header: 'Updated At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
                filterVariant: 'datetime-range',
            },
            {
                accessorFn: (user) => user.created_at,
                id: 'created_at',
                header: 'Created At',
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
                filterVariant: 'datetime-range',
            },
        ],
        [
            organizations,
            organizationColumnDef,
            organizationId,
            addMember,
            allUsers,
            users,
            updateMemberRole,
        ],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: users,
            initialState: {
                columnVisibility: {
                    id: false,
                    auth0_sub: false,
                    created_at: false,
                    organizations: organizations !== undefined,
                },
            },
            defaultColumn: {
                enableColumnFilter: true,
                enableSorting: true,
            },
            getRowId: (user) => user.id,
            enableSorting: true,
            renderTopToolbarCustomActions: ({ table }) => {
                const selectedRows = table.getSelectedRowModel().rows
                const selectedRowsOriginal = selectedRows.map(
                    (row) => row.original,
                )

                const onAddMember = () => setIsAddingMember(true)
                const onRemoveMembers = () => {
                    const removingUserIds = selectedRows.map(
                        (row) => row.original.id,
                    )
                    removeMembers?.(removingUserIds)
                }
                const onExportToCSV = () => {
                    const data = stringify(selectedRowsOriginal, {
                        header: true,
                        columns: [
                            { key: 'id', header: 'User ID' },
                            { key: 'first_name', header: 'First Name' },
                            { key: 'last_name', header: 'Last Name' },
                            { key: 'email', header: 'Email' },
                            { key: 'phone_number', header: 'Phone Number' },
                            { key: 'auth0_sub', header: 'Auth0 Sub' },
                            { key: 'created_at', header: 'Created At' },
                        ],
                    })
                    exportFile('users.csv', [data], 'text/csv')
                }
                const onExportToJSON = () => {
                    const data = JSON.stringify(selectedRowsOriginal, null, 2)
                    exportFile('users.json', [data], 'application/json')
                }

                return (
                    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        {addMember && allUsers && (
                            <Button onClick={onAddMember}>Add member</Button>
                        )}
                        {removeMember && (
                            <Button
                                disabled={selectedRows.length < 1}
                                onClick={onRemoveMembers}>
                                {selectedRows.length > 1
                                    ? `Remove ${selectedRows.length} members`
                                    : 'Remove member'}
                            </Button>
                        )}
                        <SplitButtons disabled={selectedRows.length < 1}>
                            <Button onClick={onExportToCSV}>
                                Export to CSV
                            </Button>
                            <Button onClick={onExportToJSON}>
                                Export to JSON
                            </Button>
                        </SplitButtons>
                    </Box>
                )
            },
            state: {
                isLoading,
                showProgressBars,
                columnVisibility: {
                    organizations: organizations !== undefined,
                },
            },
            enableRowActions: true,
            renderRowActions: ({ row }) => {
                const id = row.id

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
                        <Link to={`/users/${id}`}>
                            <Tooltip title="Details">
                                <span>
                                    <IconButton
                                        size="small"
                                        title="Open details">
                                        <Info fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Link>
                    </Box>
                )
            },
            isPrimaryBackgroundColor,
        },
        'users-table',
    )

    return <>
        <MaterialReactTable table={table} />
        <CreateUserDialog
            open={isAddingMember}
            users={allUsers ?? []}
            setOpen={setIsAddingMember}
            addMember={addMember}
        />
    </>
}

interface AddMemberDialogProps {
    users: APIUser[]
    open: boolean
    setOpen: (open: boolean) => void
    addMember?: (userId: string) => void
}

const CreateUserDialog = ({
    users,
    open,
    setOpen,
    addMember,
}: AddMemberDialogProps) => {
    const [userId, setUserId] = useState<string>('')

    const onCreate = () => {
        addMember?.(userId)
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setUserId('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Add member</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel
                                id="add-member-label"
                                required>
                                User
                            </InputLabel>
                            <Select
                                label="User"
                                labelId="add-member-label"
                                value={userId}
                                onChange={(event) =>
                                    setUserId(event.target.value)
                                }
                                sx={{
                                    height: 60,
                                }}
                            >
                                {users.map((user) => (
                                    <MenuItem
                                        key={user.id}
                                        value={user.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <ListItemText
                                            primary={`${user.first_name} ${user.last_name}`}
                                            secondary={user.email || user.phone_number || user.auth0_sub}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={userId === ''}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    )
}